import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { saveData } from 'services/api/saveData';
import { formatDate } from 'services/utils/dateTimeUtils';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import WarningModal from 'components/modals/WarningModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faBuilding, faUser, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/contact-merge.scss';

/*
 * ContactMerge.tsx
 * The merge preview page of two or more contacts. It shows the values of
 * every contact next to each other with the resulting values, the linked
 * objects which move to the main contact and the warnings, and executes
 * the merge after confirmation. The page is reached from the duplicates
 * notice of a contact list, the merge button of a contact page and the
 * bulk actions panel, with the contact ids in the query string.
 */

// One contact of the preview
interface MergePreviewContact {
    id: number;
    name: string;
    number: string | null;
    entity_type: string | null;
    contact_type: string;
    created: string;
    is_target: boolean;
}

// One field row of the preview
interface MergePreviewField {
    field: string;
    values: Record<string, any>;
    result: any;
    result_from: number;
}

// One entry of a collection
interface MergeCollectionEntry {
    id: number;
    label: string;
}

// One collection row of the preview
interface MergePreviewCollection {
    field: string;
    values: Record<string, MergeCollectionEntry[]>;
    result: MergeCollectionEntry[];
}

// One linked object entry of the preview
interface MergeLinkedObject {
    key: string;
    model: string;
    label: string;
    count: number;
    per_contact: Record<string, number>;
}

// One warning of the preview
interface MergeWarning {
    code: string;
    contact_id: number;
    value?: string;
}

// The preview response of the backend
interface MergePreview {
    target: number;
    contacts: MergePreviewContact[];
    fields: MergePreviewField[];
    collections: MergePreviewCollection[];
    contact_type: { values: Record<string, string>, result: string };
    linked_objects: MergeLinkedObject[];
    warnings: MergeWarning[];
}

// The fields shown as a yes or no value
const BOOLEAN_FIELDS = ['blocked', 'sms_opt_out', 'email_opt_out', 'vat_reverse_charge'];

const ContactMerge: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale } = useSettings();

    // Read the contact ids and the main contact from the query string
    const { ids, initialTarget } = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const parsedIds = (params.get('ids') || '').split(',').map(value => parseInt(value)).filter(value => !isNaN(value));
        const parsedTarget = parseInt(params.get('target') || '');
        return { ids: parsedIds, initialTarget: parsedIds.includes(parsedTarget) ? parsedTarget : parsedIds[0] };
    }, [location.search]);

    const [targetId, setTargetId] = useState<number | undefined>(initialTarget);
    const [preview, setPreview] = useState<MergePreview | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [merging, setMerging] = useState(false);

    // Fetch the preview whenever the main contact changes
    useEffect(() => {
        if (!targetId || ids.length < 2) {
            setLoading(false);
            setErrors(['validation.contact.merge.min_two']);
            return;
        }

        // Request the preview for the current main contact
        const fetchPreview = async () => {
            setLoading(true);
            try {
                const response = await saveData({
                    apiUrl: 'post_contact_merge_preview',
                    method: 'post',
                    data: { target: targetId, sources: ids.filter(id => id !== targetId) },
                });
                setPreview(response?.data ?? null);
                setErrors([]);
            } catch (error: any) {
                const data = error?.response?.data;
                setPreview(null);
                setErrors(data && typeof data === 'object' ? Object.values(data).map(value => String(value)) : ['validation.contact.merge.invalid_request']);
            }
            setLoading(false);
        };
        fetchPreview();
    }, [targetId, ids]);

    // Order the contacts with the main contact first
    const orderedContacts = useMemo(() => {
        if (!preview) return [];
        return [...preview.contacts].sort((a, b) => Number(b.is_target) - Number(a.is_target));
    }, [preview]);

    // Format a scalar value of a field for display
    const formatValue = (field: string, value: any): string => {
        if (value === null || value === undefined || value === '') return '';
        if (BOOLEAN_FIELDS.includes(field)) return value ? t('contact.merge.value_yes') : t('contact.merge.value_no');
        if (field === 'contact_type') return t(`contact.contact_type.${value}`);
        return String(value);
    };

    // Get the translated label of a linked object entry, falls back to the backend label
    const getLinkedObjectLabel = (entry: MergeLinkedObject): string => {
        if (entry.model === 'timeline_item') return t('contact.merge.linked_timeline_items_label');
        return t(`${entry.model}.general.object_name.plural`, { defaultValue: entry.label });
    };

    // Get the translated text of a warning
    const getWarningText = (warning: MergeWarning): string => {
        const contact = preview?.contacts.find(entry => entry.id === warning.contact_id);
        const value = warning.code === 'contact_type_changed' && warning.value
            ? t(`contact.contact_type.${warning.value}`)
            : warning.code === 'opt_out_inherited' && warning.value
                ? t(`contact.merge.fields.${warning.value}`)
                : warning.value || '';
        return t(`contact.merge.warnings.${warning.code}`, { name: contact?.name || '', value });
    };

    // Open the confirmation and execute the merge
    const handleMerge = () => {
        if (!preview || !targetId) return;
        initializeModal(
            <WarningModal
                header='contact.merge.confirm_title'
                message='contact.merge.confirm_message'
                messageValues={{ count: String(ids.length - 1), name: preview.contacts.find(contact => contact.is_target)?.name || '' }}
                buttonLabel='button.merge_button_label'
                onApproval={executeMerge} />,
            { modalSize: 'extra-small' }
        );
    };

    // Execute the merge and open the merged contact
    const executeMerge = async () => {
        if (!targetId) return;
        setMerging(true);
        try {
            const response = await saveData({
                apiUrl: 'post_contact_merge',
                method: 'post',
                data: { target: targetId, sources: ids.filter(id => id !== targetId) },
            });
            setFloatingAlert({ type: 'success', message: 'contact.merge.merge_success_message' });
            const entityType = response?.data?.entity_type || 'contact';
            history.push(`/contacts/${entityType}/${response?.data?.id || targetId}`);
        } catch (error: any) {
            const data = error?.response?.data;
            setErrors(data && typeof data === 'object' ? Object.values(data).map(value => String(value)) : ['validation.contact.merge.invalid_request']);
            setMerging(false);
        }
    };

    // Render the header cell of a contact with the main contact radio
    const renderContactHeader = (contact: MergePreviewContact) => (
        <th key={contact.id} className={`contact-merge-contact-header ${contact.is_target ? 'is-target' : ''}`}>
            <label className='contact-merge-target-choice'>
                <input type='radio'
                       name='merge-target'
                       checked={contact.is_target}
                       onChange={() => setTargetId(contact.id)} />
                <span>{t('contact.merge.main_contact_label')}</span>
            </label>
            <div className='contact-merge-contact-name'>
                <FontAwesomeIcon icon={contact.entity_type === 'person' ? faUser : faBuilding} />
                <span>{contact.name}</span>
            </div>
            <div className='contact-merge-contact-meta'>
                {t(`contact.contact_type.${contact.contact_type}`)}
                {contact.number ? ` · ${contact.number}` : ''}
                {contact.created ? ` · ${t('contact.merge.created_label')} ${formatDate(contact.created, userLocale)}` : ''}
            </div>
        </th>
    );

    // Render one field row with the value per contact and the result
    const renderFieldRow = (row: MergePreviewField) => {
        const resultText = formatValue(row.field, row.result);
        return (
            <tr key={row.field}>
                <td className='contact-merge-field-label'>{t(`contact.merge.fields.${row.field}`)}</td>
                {orderedContacts.map(contact => {
                    const valueText = formatValue(row.field, row.values[contact.id]);
                    const dropped = valueText !== '' && valueText !== resultText;
                    return (
                        <td key={contact.id} className={`contact-merge-value ${dropped ? 'is-dropped' : ''} ${row.result_from === contact.id && valueText !== '' ? 'is-kept' : ''}`}>
                            {valueText || <span className='contact-merge-empty'>–</span>}
                        </td>
                    );
                })}
                <td className='contact-merge-value contact-merge-result'>
                    {resultText || <span className='contact-merge-empty'>–</span>}
                </td>
            </tr>
        );
    };

    // Render one collection row with the entries per contact and the merged result
    const renderCollectionRow = (row: MergePreviewCollection) => (
        <tr key={row.field}>
            <td className='contact-merge-field-label'>{t(`contact.merge.fields.${row.field}`)}</td>
            {orderedContacts.map(contact => (
                <td key={contact.id} className='contact-merge-value'>
                    {(row.values[contact.id] || []).length > 0
                        ? (row.values[contact.id] || []).map(entry => <div key={entry.id}>{entry.label}</div>)
                        : <span className='contact-merge-empty'>–</span>}
                </td>
            ))}
            <td className='contact-merge-value contact-merge-result'>
                {row.result.length > 0
                    ? row.result.map(entry => <div key={entry.id}>{entry.label}</div>)
                    : <span className='contact-merge-empty'>–</span>}
            </td>
        </tr>
    );

    return (
        <div className='contact-merge-page container-filled'>
            <div className='contact-merge-header'>
                <div className='contact-merge-title'>
                    <span className='back-button' onClick={() => history.goBack()}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </span>
                    <h2 className='fixed-title'>{t('contact.merge.page_title')}</h2>
                </div>
                <div className='button-container right-align'>
                    <SecondaryButton
                        onClick={() => history.goBack()}
                        label='general.cancel'
                        size='medium' />
                    <PrimaryButton
                        onClick={handleMerge}
                        label='button.merge_button_label'
                        size='medium'
                        loading={merging}
                        disabled={{ disabled: !preview || loading || errors.length > 0 }} />
                </div>
            </div>
            <p className='contact-merge-explanation'>{t('contact.merge.page_explanation')}</p>

            {errors.length > 0 && (
                // Show the blocking validation errors of the merge
                <div className='contact-merge-warnings is-error'>
                    {errors.map((error, index) => (
                        <div key={index} className='contact-merge-warning'>
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            <span>{t(error, { defaultValue: error })}</span>
                        </div>
                    ))}
                </div>
            )}

            {preview && preview.warnings.length > 0 && (
                // Show the warnings about dropped or changed values
                <div className='contact-merge-warnings'>
                    {preview.warnings.map((warning, index) => (
                        <div key={index} className='contact-merge-warning'>
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            <span>{getWarningText(warning)}</span>
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <div className='loader'></div>
            ) : preview && (
                <>
                    <div className='card contact-merge-card'>
                        <div className='contact-merge-table-wrapper'>
                            <table className='contact-merge-table'>
                                <thead>
                                    <tr>
                                        <th className='contact-merge-field-label'></th>
                                        {orderedContacts.map(renderContactHeader)}
                                        <th className='contact-merge-result-header'>
                                            <div className='contact-merge-contact-name'>{t('contact.merge.result_label')}</div>
                                            <div className='contact-merge-contact-meta'>{t('contact.merge.result_explanation')}</div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderFieldRow({
                                        field: 'contact_type',
                                        values: preview.contact_type.values,
                                        result: preview.contact_type.result,
                                        result_from: preview.target,
                                    })}
                                    {preview.fields.map(renderFieldRow)}
                                    {preview.collections.map(renderCollectionRow)}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className='card contact-merge-linked-card'>
                        <div className='header'>
                            <h6>{t('contact.merge.linked_objects_title')}</h6>
                        </div>
                        {preview.linked_objects.length === 0 ? (
                            <p className='contact-merge-linked-empty'>{t('contact.merge.linked_objects_empty')}</p>
                        ) : (
                            <>
                                <p className='contact-merge-linked-explanation'>{t('contact.merge.linked_objects_explanation')}</p>
                                <div className='contact-merge-linked-list'>
                                    {preview.linked_objects.map(entry => {
                                        const movingCount = orderedContacts
                                            .filter(contact => !contact.is_target)
                                            .reduce((sum, contact) => sum + (entry.per_contact[contact.id] || 0), 0);
                                        return (
                                            <div key={entry.key} className='contact-merge-linked-item'>
                                                <span className='contact-merge-linked-count'>{movingCount}</span>
                                                <span className='contact-merge-linked-label'>{getLinkedObjectLabel(entry)}</span>
                                                <span className='contact-merge-linked-total'>
                                                    {t('contact.merge.linked_total_label', { count: entry.count })}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ContactMerge;
